import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { generateArticle, getAIModels, suggestPhotoOrder } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { X, Loader2, Sparkles, Image, Settings, ChevronRight, Star, MessageSquare } from "lucide-react";

export function BatchArticleGenerator({ photos, categories, destinations, onClose, onSuccess, getPhotoUrl }) {
  const navigate = useNavigate();
  const [aiModels, setAIModels] = useState(null);
  const [orderedPhotos, setOrderedPhotos] = useState(photos);
  const [coverPhotoId, setCoverPhotoId] = useState(photos[0]?.id || null);
  const [suggestingOrder, setSuggestingOrder] = useState(false);
  const [form, setForm] = useState({
    category_id: "",
    destination_id: "",
    content_type: "article",
    location: "",
    venue_name: "",
    company: "",
    dates: "",
    ambiance: "",
    highlights: "",
    lowlights: "",
    practical_info: "",
    editorial_angle: "",
    tone: "Chaleureux et enthousiaste",
    keywords: "",
    must_mention: "",
    length: "long",
    seo_optimized: true,
    ai_model: "gpt-4o"
  });

  useEffect(() => {
    loadAIModels();
  }, []);

  const loadAIModels = async () => {
    try {
      const res = await getAIModels();
      setAIModels(res.data);
      setForm(prev => ({ ...prev, ai_model: res.data.default_model }));
    } catch (error) {
      console.error("Error loading AI models:", error);
    }
  };

  const handleSuggestOrder = async () => {
    setSuggestingOrder(true);
    try {
      const res = await suggestPhotoOrder(photos.map(p => p.id));
      const newOrder = res.data.suggested_photo_ids.map(id => 
        photos.find(p => p.id === id)
      ).filter(Boolean);
      photos.forEach(p => {
        if (!newOrder.find(op => op.id === p.id)) {
          newOrder.push(p);
        }
      });
      setOrderedPhotos(newOrder);
      if (res.data.cover_photo_id) {
        setCoverPhotoId(res.data.cover_photo_id);
      }
      toast.success(`Ordre suggéré: ${res.data.reasoning || "Optimisé pour le récit"}`);
    } catch (error) {
      toast.error("Erreur lors de la suggestion");
    } finally {
      setSuggestingOrder(false);
    }
  };

  const movePhoto = (index, direction) => {
    const newOrder = [...orderedPhotos];
    const newIndex = index + direction;
    if (newIndex >= 0 && newIndex < newOrder.length) {
      [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
      setOrderedPhotos(newOrder);
    }
  };

  const setCover = (photoId) => {
    setCoverPhotoId(photoId);
    const photo = orderedPhotos.find(p => p.id === photoId);
    if (photo) {
      const newOrder = [photo, ...orderedPhotos.filter(p => p.id !== photoId)];
      setOrderedPhotos(newOrder);
    }
  };

  const handleGenerate = async () => {
    // Redirect to AI Editor with context instead of generating directly
    const category = categories.find(c => c.id === form.category_id);
    const destination = destinations.find(d => d.id === form.destination_id);
    
    const generationContext = {
      photos: orderedPhotos,
      category_id: form.category_id,
      category_name: category?.name,
      destination_id: form.destination_id || undefined,
      destination_name: destination?.name,
      content_type: form.content_type || "article",
      location: form.location || undefined,
      venue_name: form.venue_name || undefined,
      company: form.company || undefined,
      dates: form.dates || undefined,
      ambiance: form.ambiance || undefined,
      highlights: form.highlights || undefined,
      lowlights: form.lowlights || undefined,
      practical_info: form.practical_info || undefined,
      editorial_angle: form.editorial_angle || undefined,
      tone: form.tone,
      keywords: form.keywords ? form.keywords.split(",").map(k => k.trim()).filter(k => k) : undefined,
      must_mention: form.must_mention || undefined,
      length: form.length,
      seo_optimized: form.seo_optimized,
      ai_model: form.ai_model,
      cover_photo_index: orderedPhotos.findIndex(p => p.id === coverPhotoId)
    };
    
    // Close modal and navigate to AI Editor
    onClose();
    navigate("/ai-editor", { state: generationContext });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-5xl max-h-[95vh] overflow-auto">
        <div className="p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent" />
                Générer un article avec ChatGPT
              </h2>
              <p className="text-sm text-muted-foreground">{orderedPhotos.length} photo(s) • Modèle: {form.ai_model}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        <div className="p-6">
          {/* SECTION 1: Photos avec ordre et couverture */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-blue-900 flex items-center gap-2">
                <Image className="h-4 w-4" />
                Ordre des photos (la première = couverture)
              </h3>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleSuggestOrder}
                disabled={suggestingOrder}
                className="text-blue-600 border-blue-300"
              >
                {suggestingOrder ? (
                  <><Loader2 className="h-3 w-3 animate-spin mr-1" /> Analyse...</>
                ) : (
                  <><Sparkles className="h-3 w-3 mr-1" /> Suggestion IA</>
                )}
              </Button>
            </div>
            <p className="text-xs text-blue-700 mb-3">
              Utilisez les flèches pour réordonner. Cliquez sur l'étoile pour définir la couverture.
            </p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {orderedPhotos.map((photo, idx) => (
                <div 
                  key={photo.id}
                  className={`relative shrink-0 group ${
                    photo.id === coverPhotoId ? "ring-2 ring-yellow-400 ring-offset-2" : ""
                  }`}
                >
                  <img 
                    src={getPhotoUrl(photo, "thumbnail")}
                    alt={photo.filename}
                    className="h-24 w-24 object-cover rounded"
                  />
                  {photo.id === coverPhotoId && (
                    <div className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 text-xs px-1 rounded font-bold">
                      COUV
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 bg-black/70 text-white text-xs px-1.5 rounded">
                    {idx + 1}
                  </div>
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                    {idx > 0 && (
                      <button onClick={() => movePhoto(idx, -1)} className="p-1 bg-white rounded">
                        <ChevronRight className="h-4 w-4 rotate-180" />
                      </button>
                    )}
                    {photo.id !== coverPhotoId && (
                      <button onClick={() => setCover(photo.id)} className="p-1 bg-yellow-400 rounded" title="Définir comme couverture">
                        <Star className="h-4 w-4" />
                      </button>
                    )}
                    {idx < orderedPhotos.length - 1 && (
                      <button onClick={() => movePhoto(idx, 1)} className="p-1 bg-white rounded">
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 2: Modèle IA et Ton */}
          <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h3 className="font-medium text-purple-900 mb-3 flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Paramètres IA
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-purple-800">Modèle ChatGPT</label>
                <Select value={form.ai_model} onValueChange={v => setForm(prev => ({...prev, ai_model: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {aiModels?.available_models?.map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-purple-800">Ton de l'article</label>
                <Select value={form.tone} onValueChange={v => setForm(prev => ({...prev, tone: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {aiModels?.tone_presets?.map(t => (
                      <SelectItem key={t.id} value={t.name}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-purple-800">Longueur</label>
                <Select value={form.length} onValueChange={v => setForm(prev => ({...prev, length: v}))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Court (300-500 mots)</SelectItem>
                    <SelectItem value="medium">Moyen (600-900 mots)</SelectItem>
                    <SelectItem value="long">Long (1200+ mots)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left column - Context */}
            <div className="space-y-4">
              <h3 className="font-medium text-primary">Contexte du voyage</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Catégorie</label>
                  <Select value={form.category_id} onValueChange={v => setForm(prev => ({...prev, category_id: v}))}>
                    <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Destination</label>
                  <Select value={form.destination_id} onValueChange={v => setForm(prev => ({...prev, destination_id: v}))}>
                    <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                    <SelectContent>
                      {destinations.map(dest => (
                        <SelectItem key={dest.id} value={dest.id}>{dest.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Nom (bateau / hôtel / restaurant)</label>
                <Input 
                  value={form.venue_name} 
                  onChange={e => setForm(prev => ({...prev, venue_name: e.target.value}))}
                  placeholder="Ex: MSC Meraviglia, Hôtel Negresco..."
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Compagnie</label>
                  <Input 
                    value={form.company} 
                    onChange={e => setForm(prev => ({...prev, company: e.target.value}))}
                    placeholder="MSC, Costa..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Dates</label>
                  <Input 
                    value={form.dates} 
                    onChange={e => setForm(prev => ({...prev, dates: e.target.value}))}
                    placeholder="15-22 mars 2024"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Ambiance ressentie</label>
                <Input 
                  value={form.ambiance} 
                  onChange={e => setForm(prev => ({...prev, ambiance: e.target.value}))}
                  placeholder="Festive, romantique, familiale..."
                />
              </div>
            </div>

            {/* Right column - Editorial */}
            <div className="space-y-4">
              <h3 className="font-medium text-primary">Consignes éditoriales</h3>
              
              <div>
                <label className="block text-sm font-medium mb-1">Ce qu'on a adoré (un par ligne)</label>
                <Textarea 
                  value={form.highlights} 
                  onChange={e => setForm(prev => ({...prev, highlights: e.target.value}))}
                  rows={3}
                  placeholder="La piscine panoramique&#10;Le restaurant gastronomique&#10;L'équipage aux petits soins"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Points d'attention (un par ligne)</label>
                <Textarea 
                  value={form.lowlights} 
                  onChange={e => setForm(prev => ({...prev, lowlights: e.target.value}))}
                  rows={2}
                  placeholder="WiFi un peu lent&#10;File d'attente au buffet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Instructions spéciales</label>
                <Textarea 
                  value={form.editorial_angle} 
                  onChange={e => setForm(prev => ({...prev, editorial_angle: e.target.value}))}
                  rows={2}
                  placeholder="Mettre l'accent sur..., Ne pas mentionner..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">À mentionner absolument</label>
                <Input 
                  value={form.must_mention} 
                  onChange={e => setForm(prev => ({...prev, must_mention: e.target.value}))}
                  placeholder="Le spa, la croisière au coucher du soleil..."
                />
              </div>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>Annuler</Button>
            <Button 
              className="bg-accent hover:bg-accent/90"
              onClick={handleGenerate}
              disabled={!form.category_id}
            >
              <MessageSquare className="h-4 w-4 mr-2" /> Ouvrir l'éditeur IA
            </Button>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-4">
            Vous pourrez dialoguer avec l'IA pour ajuster le contenu avant l'intégration au site.
          </p>
        </div>
      </div>
    </div>
  );
}
